import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import {getRequest, postRequest} from '../../../services/base'
import {useParams} from 'react-router-dom'
import ReferenceSelector from './ReferenceSelector'
import {ConnectorViewMappingContext} from './context/context'
import ConnectorSearchFilter from './ConnectorSearchFilter'
import {NavContext} from '../../../context/global_context'
import {notifyError, notifySuccess} from '../../../utils/notifications'
import {Form, Table, Tabs} from 'antd'
import Button from '../../common/Button'
import NoRecordsFound from '../../common/NoRecordsFound'
import InputText from '../../common/InputText'

const ConnectorMappingForm = (props) => {
  const intl = useIntl();
  const [connVwMpState, dispatch] = useContext(ConnectorViewMappingContext)
  const [, navDispatch] = useContext(NavContext)

  const { connector_view, vehicle } = useParams()
  const [activeTab, setActiveTab] = useState('pending')
  const [nameSearch, setNameSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [connectors, setConnectors] = useState([])
  const [pendingConnectors, setPendingConnectors] = useState([])
  const [mappedConnectors, setMappedConnectors] = useState([])
  const [loader, setLoader] = useState(false)
  const listContentRef = useRef(null)
  const [tableBodyHeight, setTableBodyHeight] = useState(0)
  const showConnectorList = !connVwMpState?.connector?.id

  useEffect(() => {
    const el = listContentRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setTableBodyHeight(entry.contentRect.height - 31)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [showConnectorList])

  useEffect(() => {
    getConnectorsData()
  }, [])

  const getConnectorsData = async (reference, connector, hide_sidebar) => {
    setLoader(true)
    try {
      let res = await getRequest(
        `/api/connector_views/connectors/${connector_view}/`
      )
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors - the failure branch has to be explicit.
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.fetchConnectorsFailed'}))
        return
      }
      setConnectors(res.data)
      setMappedConnectors(
        res.data?.length > 0 ? res.data?.filter((ctr) => ctr.auto_mapped) : []
      )
      // Re-apply current filters to pending connectors after re-fetch
      applyFilters(res.data, nameSearch, selectedCategory?.value || '')
      let tempMappedConnectors =
        res.data?.length > 0 ? res.data?.filter((ctr) => ctr.auto_mapped) : []
      formatMappedReferences(
        tempMappedConnectors,
        reference,
        connector,
        hide_sidebar
      )
    } catch (e) {
      notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.fetchConnectorsFailed'}))
    } finally {
      setLoader(false)
    }
  }

  const formatMappedReferences = (data, reference, connector, hide_sidebar) => {
    let tempMappedReferences = {}
    data.forEach((row) => {
      if (row.auto_mapped in tempMappedReferences) {
        tempMappedReferences[row.auto_mapped].push(row.name)
      } else {
        tempMappedReferences[row.auto_mapped] = [row.name]
      }
    })
    if (reference) {
      dispatch({
        type: 'update_mapping_and_reference',
        data: {
          connector: hide_sidebar
            ? {}
            : {
                ...connector,
                auto_mapped: null,
              },
          svg_group_reference: null,
          selected_reference: null,
          connector_view: {
            ...connVwMpState.connector_view,
            references: reference,
          },
          mappedReferences: tempMappedReferences,
        },
      })
    } else {
      dispatch({ type: 'mappedReferences', data: tempMappedReferences })
    }
  }

  const onSelectMapping = (connector) => {
    dispatch({
      type: 'select_mapping',
      data: {
        connector: connector,
        svg_group_reference: connector.svg_group_reference,
        selected_reference: connector.auto_mapped,
      },
    })
  }

  const updateMapping = async (connector, ref, hide_sidebar = true) => {
    setLoader(true)
    try {
      let res = await postRequest(
        `/api/connector_views/save_connector_view_mapping/${connector_view}/`,
        {
          name: connector.name,
          connector: connector.id,
          reference: ref,
          prev_reference: connector.auto_mapped,
        }
      )
      if (res?.status) {
        notifySuccess(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.updateSuccess'}))
        if (ref) {
          document.getElementById(`${ref}`).classList.add('mappedSvgElement')
        } else {
          document
            .getElementById(`${connector.auto_mapped}`)
            ?.classList?.remove('mappedSvgElement')
        }
        getConnectorsData(res.svg_group_reference, connector, hide_sidebar)
      } else {
        notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.updateFailed'}))
      }
    } catch (e) {
      notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.updateFailed'}))
    } finally {
      setLoader(false)
    }
  }

  const connectorRefSet = useMemo(
    () => new Set(connectors.map((c) => c.reference)),
    [connectors]
  )

  const unmappedRefs = useMemo(
    () =>
      connVwMpState?.connector_view?.group_ids?.filter(
        (ref) => !connectorRefSet.has(ref)
      ) || [],
    [connVwMpState?.connector_view?.group_ids, connectorRefSet]
  )

  const connectorsToRender =
    activeTab === 'pending' ? pendingConnectors : mappedConnectors

  const dataSource = useMemo(
    () =>
      connectorsToRender.map((connector, idx) => ({
        ...connector,
        key: idx,
      })),
    [connectorsToRender]
  )

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({id: 'connectorViews.mapping.selectConnector'}),
        dataIndex: 'name',
        render: (_, record) => {
          let markedAs = ''
          if (record.edited) {
            markedAs = <span className="connector-mapping-item-mark"> &#9998; </span>
          } else if (record.is_manual_mapped === '') {
            markedAs = ''
          } else if (record.is_manual_mapped === true) {
            markedAs = <span className="connector-mapping-item-mark"> A </span>
          } else {
            markedAs = <span className="connector-mapping-item-mark"> M </span>
          }

          return (
            <div className="connector-mapping-cell">
              <div className="connector-mapping-cell-content">
                <div className="connector-mapping-cell-title bolder">
                  {markedAs} {record.name}
                </div>
                <div className="connector-mapping-item-desc">{record.description}</div>
                {record.auto_mapped !== null ? (
                  <>
                    <div className="connector-mapping-item-desc">
                      {intl.formatMessage({id: 'connectorViews.mapping.threeDReference'})}:&nbsp;
                      {record.svg_group_reference
                        ? record.svg_group_reference
                        : '-'}
                    </div>
                    {record.auto_mapped ? (
                      <div className="connector-mapping-item-desc">
                        {intl.formatMessage({id: 'connectorViews.mapping.svgObject'})}:&nbsp;
                        <span
                          style={{
                            color:
                              connVwMpState.selected_svg_object === record.auto_mapped
                                ? 'var(--color-form-link-danger)'
                                : 'inherit',
                          }}
                          id={`auto-mapped-${record.auto_mapped}`}
                        >
                          {record.auto_mapped ? record.auto_mapped : '-'}
                        </span>
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                ) : (
                  ''
                )}
              </div>
              <div className="connector-mapping-cell-action">
                <Button
                  type="link"
                  className={`link-button${connVwMpState?.svgLoaded ? '' : ' disabled'}`}
                  disabled={!connVwMpState?.svgLoaded}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelectMapping(record)
                  }}
                >
                  {record.auto_mapped ? intl.formatMessage({id: 'connectorViews.mapping.edit'}) : intl.formatMessage({id: 'connectorViews.mapping.select'})}
                </Button>
              </div>
            </div>
          )
        },
      },
    ],
    [connVwMpState?.svgLoaded, connVwMpState.selected_svg_object, activeTab]
  )

  // Shared filter function: applies both name search and category filters
  const applyFilters = (allConnectors, search, category) => {
    setPendingConnectors(
      allConnectors?.length > 0
        ? allConnectors?.filter((ctr) => {
            if (ctr.auto_mapped) return false
            const matchesSearch = !search || ctr.name.toUpperCase().includes(search)
            const matchesCategory = !category || ctr.category.includes(category)
            return matchesSearch && matchesCategory
          })
        : []
    )
  }

  const handleNameSearch = useCallback(
    (e) => {
      const name = e.toUpperCase()
      setNameSearch(name)
      const category = selectedCategory?.value || ''
      applyFilters(connectors, name, category)
    },
    [connectors, selectedCategory]
  )

  const handleCategoryChange = (value) => {
    setSelectedCategory(value)
  }

  const handleCategorySearch = () => {
    const search = nameSearch
    const category = selectedCategory?.value || ''
    applyFilters(connectors, search, category)
  }

  const handleCategoryClear = () => {
    setSelectedCategory(null)
    applyFilters(connectors, nameSearch, '')
  }

  return (
    <div className="connector-mapping-left-panel">
      {connVwMpState?.connector?.id ? (
        <ReferenceSelector
          connector={connVwMpState?.connector}
          unmappedReferences={unmappedRefs}
          onClick={(connector, ref, hide_sidebar) => {
            updateMapping(connector, ref, hide_sidebar)
          }}
          sid={vehicle}
          connectorViewId={connector_view}
        />
      ) : (
        <>
          <Tabs
            className="connector-mapping-tabs"
            activeKey={activeTab}
            animated={false}
            tabBarGutter={0}
            items={[
              { key: 'pending', label: intl.formatMessage({id: 'connectorViews.mapping.pending'}, {count: pendingConnectors.length}) },
              { key: 'autoMapped', label: intl.formatMessage({id: 'connectorViews.mapping.mapped'}, {count: mappedConnectors.length}) },
            ]}
            onChange={(value) => setActiveTab(value)}
          />

          <div className="connector-mapping-list-wrapper">
            {activeTab === 'pending' && (
              <div className="connector-mapping-search-input">
                <Form>
                  <InputText
                    key={connVwMpState?.connector?.id ? 'selected' : 'list'}
                    type="search"
                    name="search_connector"
                    placeholder={intl.formatMessage({id: 'connectorViews.mapping.searchConnectorPlaceholder'})}
                    isRequired={false}
                    initialValue={nameSearch}
                    onSubmit={(e) => handleNameSearch(e)}
                  />
                </Form>
              </div>
            )}
            <div className="connector-mapping-list-content" ref={listContentRef} data-testid="vv-connector-list">
              <div className="connector-mapping-table-header semi-bold">
                <span>{intl.formatMessage({id: 'connectorViews.mapping.selectConnector'})}</span>
                {activeTab === 'pending' && (
                  <ConnectorSearchFilter
                    filterConnector={handleCategorySearch}
                    selectedCategory={selectedCategory}
                    onCategoryChange={handleCategoryChange}
                    onClear={handleCategoryClear}
                  />
                )}
              </div>
              <Table
                className="connector-mapping-table"
                rowKey="key"
                columns={columns}
                dataSource={dataSource}
                loading={loader}
                pagination={false}
                size="small"
                showHeader={false}
                scroll={{ y: tableBodyHeight }}
                locale={{
                  emptyText: <NoRecordsFound loading={false} />,
                }}
                onRow={(record) => ({
                  onClick: () => onSelectMapping(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ConnectorMappingForm
